import {
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Input,
  Modal,
  Pagination,
  Popover,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
} from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import { ListFilter, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentAuthUser } from "../../auth";
import { canAccessCrmOperation, canUsePermission } from "../../../lib/auth";
import { getProvinceOptions } from "../../../shared/region";
import {
  formatAccessStatus,
  formatAssignmentMode,
  formatTargetType,
  formatTaskStatus,
  formatTaskType,
} from "../service/crmCollaborationTaskService";
import {
  CRM_COLLABORATION_PAGE_SIZE_OPTIONS,
  EMPTY_CRM_COLLABORATION_POOL_FILTERS,
  useCrmCollaborationTaskRuntime,
} from "../runtime/useCrmCollaborationTaskRuntime";
import { SearchHistoryInput } from "../../../shared/ui/SearchHistoryInput";
import {
  ContactEditor,
  createContactDraft,
  getMobileValidationError,
  mapContactDraftToPayload,
  type ContactEditorDraft,
} from "../../crm_contact_workbench/ui/ContactEditorPanel";
import {
  ContactRecordCreator,
  ContactRecordList,
  createDefaultContactRecordDraft,
  mapContactRecordDraftToPayload,
  type ContactRecordDraft,
} from "../../crm_contact_workbench/ui/ContactRecordPanels";
import type {
  ContactWorkbenchItem,
  ContactWorkbenchRecordItem,
  ContactWorkbenchTemporaryTaskAssignment,
} from "../../crm_contact_workbench/types";
import type {
  CrmCollaborationPoolListFilters,
  CrmCollaborationStaffOption,
  CrmCollaborationTask,
  CrmCollaborationTaskItem,
  CrmCollaborationTaskOriginalOwnerOption,
  CrmCollaborationTaskPublisherOption,
} from "../types";
import { CrmCollaborationTaskImportPreviewCard } from "./CrmCollaborationTaskImportPreviewCard";
import "./CrmCollaborationTaskPage.css";
import "../../crm_contact_workbench/ui/CrmContactWorkbenchPage.css";
import "../../../shared/ui/crmListPageLayout.css";
import "../../../shared/ui/adminEditorForm.css";

type CrmCollaborationTaskTabKey = "tasks" | "tele_sales" | "strong_follow" | "my_items";

type CrmCollaborationTaskFeaturePageProps = {
  initialTab?: CrmCollaborationTaskTabKey;
};

type MyContactsTaskType = "all" | "tele_sales" | "strong_follow";

const EMPTY_PUBLISHER_OPTIONS: CrmCollaborationTaskPublisherOption[] = [];
const EMPTY_ORIGINAL_OWNER_OPTIONS: CrmCollaborationTaskOriginalOwnerOption[] = [];

/** CRM 协作任务池页面主视图。 */
export function CrmCollaborationTaskFeaturePage({
  initialTab = "tasks",
}: CrmCollaborationTaskFeaturePageProps = {}) {
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUsePermission(currentUser, "crm.collaboration:manage");
  const canAccessContacts = canUsePermission(currentUser, "crm.contact:access");
  const canViewStaffOptions = canUsePermission(currentUser, "system.dept_user:access");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [myContactsTaskType, setMyContactsTaskType] = useState<MyContactsTaskType>("all");
  const [onlyMyContacts, setOnlyMyContacts] = useState(true);
  const runtime = useCrmCollaborationTaskRuntime(
    currentUser?.id,
    activeTab === "tasks" && canManage && canViewStaffOptions,
    activeTab === "my_items" && canAccessContacts,
    myContactsTaskType,
    onlyMyContacts,
  );
  const [recordDraft, setRecordDraft] = useState<ContactRecordDraft>(
    createDefaultContactRecordDraft,
  );
  const [contactDraft, setContactDraft] = useState<ContactEditorDraft | null>(null);
  const [deleteConfirmRecord, setDeleteConfirmRecord] =
    useState<ContactWorkbenchRecordItem | null>(null);
  const selectedContact = runtime.myContacts.find(
    (item) => item.id === runtime.selectedContactId,
  );
  const canRecordSelectedContact = canAccessCrmOperation(
    currentUser,
    "crm.contact:record",
    Boolean(selectedContact?.canRecord),
  );
  const canEditSelectedContact = canAccessCrmOperation(
    currentUser,
    "crm.contact:access",
    Boolean(selectedContact?.canEdit),
  );
  const canDeleteSelectedContactRecord = canAccessCrmOperation(
    currentUser,
    "crm.contact:record",
    Boolean(selectedContact?.canDeleteRecord),
  );

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!runtime.selectedContactDetail) {
      setContactDraft(null);
      return;
    }
    setContactDraft(createContactDraft(runtime.selectedContactDetail));
  }, [runtime.selectedContactDetail]);

  useEffect(() => {
    const firstTypeCode = runtime.contactMetadata?.recordTypeOptions[0]?.code;
    if (!firstTypeCode) return;
    setRecordDraft((current) =>
      runtime.contactMetadata?.recordTypeOptions.some(
        (option) => option.code === current.recordTypeCode,
      )
        ? current
        : { ...current, recordTypeCode: firstTypeCode },
    );
  }, [runtime.contactMetadata?.recordTypeOptions]);

  /** 新增联系人动态并清空表单。 */
  async function handleCreateRecord() {
    const payload = mapContactRecordDraftToPayload(recordDraft);
    if (!payload) return;
    await runtime.handleCreateRecord(payload);
    setRecordDraft(createDefaultContactRecordDraft());
  }

  /** 保存联系人资料。 */
  async function handleSaveContact() {
    if (!contactDraft) return;
    if (
      !contactDraft.orgId ||
      !contactDraft.contactName.trim() ||
      getMobileValidationError(contactDraft.mobile)
    ) {
      return;
    }
    await runtime.handleUpdateContact(mapContactDraftToPayload(contactDraft));
  }

  /** 删除抽屉内选中的联系人动态。 */
  async function handleDeleteRecord() {
    if (!deleteConfirmRecord) return;
    await runtime.handleDeleteRecord(deleteConfirmRecord.id);
    setDeleteConfirmRecord(null);
  }

  return (
    <main className="crm-list-page crm-collaboration-task-page">
      {runtime.errorMessage ? (
        <section role="alert" className="crm-collaboration-task-error">
          {runtime.errorMessage}
        </section>
      ) : null}

      <Tabs type="rounded" destroyOnHide activeTab={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane key="tasks" title="协作任务批次">
          {canManage ? <section className="crm-collaboration-task-layout">
            <section className="crm-list-panel crm-collaboration-task-card">
              <div className="crm-list-section-header">
                <h2>新建任务并导入 ID</h2>
              </div>
              <div className="crm-list-panel-body">
              <div className="crm-collaboration-task-form">
                <Input
                  placeholder="任务名称，例如：王莹负责联系人电销_2026年6月7日"
                  value={runtime.draft.taskName}
                  onChange={(taskName) => runtime.setDraft({ ...runtime.draft, taskName })}
                />
                <Select
                  value={runtime.draft.taskType}
                  onChange={(taskType) => runtime.setDraft({ ...runtime.draft, taskType })}
                >
                  <Select.Option value="tele_sales">电销</Select.Option>
                  <Select.Option value="strong_follow">强跟</Select.Option>
                </Select>
                <Input value="联系人" disabled />
                <Select
                  value={runtime.draft.assignmentMode}
                  onChange={(assignmentMode) => runtime.setDraft({ ...runtime.draft, assignmentMode })}
                >
                  <Select.Option value="pool">公共池</Select.Option>
                  <Select.Option value="specified">指定处理人</Select.Option>
                  <Select.Option value="mixed">混合</Select.Option>
                </Select>
                <StaffSelect
                  testId="crm-collaboration-owner-user-select"
                  placeholder="选择任务发起员工"
                  value={runtime.draft.ownerUserId}
                  options={runtime.staffOptions}
                  loading={runtime.staffOptionsLoading}
                  onChange={(ownerUserId) => {
                    if (!ownerUserId) {
                      return;
                    }
                    runtime.setDraft({ ...runtime.draft, ownerUserId });
                  }}
                />
                <StaffSelect
                  testId="crm-collaboration-default-assigned-user-select"
                  placeholder="选择默认处理员工，可空"
                  allowClear
                  value={runtime.draft.defaultAssignedUserId}
                  options={runtime.staffOptions}
                  loading={runtime.staffOptionsLoading}
                  onChange={(defaultAssignedUserId) =>
                    runtime.setDraft({
                      ...runtime.draft,
                      defaultAssignedUserId,
                    })
                  }
                />
                <Upload
                  accept=".txt,.csv"
                  autoUpload={false}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    void readImportTextFile(file).then(runtime.appendRawText);
                    return false;
                  }}
                >
                  <Button>上传 ID 文件</Button>
                </Upload>
                <Input.TextArea
                  placeholder="粘贴联系人 ID，支持空格、换行、逗号分隔"
                  value={runtime.draft.rawText}
                  autoSize={{ minRows: 5, maxRows: 8 }}
                  onChange={(rawText) => runtime.setDraft({ ...runtime.draft, rawText })}
                />
                <Input.TextArea
                  placeholder="备注，可空"
                  value={runtime.draft.remark}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  onChange={(remark) => runtime.setDraft({ ...runtime.draft, remark })}
                />
                <Button
                  type="primary"
                  loading={runtime.mutating}
                  disabled={!runtime.draft.taskName.trim() || !runtime.draft.rawText.trim()}
                  onClick={() => void runtime.handleCreateAndImport()}
                >
                  创建草稿并校验
                </Button>
              </div>
              </div>
            </section>

            <CrmCollaborationTaskImportPreviewCard
              result={runtime.latestImportResult}
              confirming={runtime.mutating}
              onConfirm={() => void runtime.handleConfirmImport()}
            />
          </section> : null}
          <TaskTable
            items={runtime.tasks}
            loading={runtime.loading}
            canManage={canManage}
            onPublish={(taskId) => void runtime.handlePublishTask(taskId)}
            onCancel={(taskId) => void runtime.handleCancelTask(taskId)}
          />
        </Tabs.TabPane>

        <Tabs.TabPane key="tele_sales" title="电销任务池">
          <ItemTable
            canManage={canManage}
            items={runtime.teleSalesItems}
            publisherOptions={runtime.teleSalesPublisherOptions}
            originalOwnerOptions={runtime.teleSalesOriginalOwnerOptions}
            filters={runtime.teleSalesFilters}
            filterStorageKey="crm-collaboration-tele-sales-keyword"
            emptyText={
              runtime.teleSalesViewScope === "mine"
                ? "当前暂无你作为原负责人的电销任务"
                : "当前暂无可见电销任务"
            }
            viewScope={runtime.teleSalesViewScope}
            onViewScopeChange={(scope) => {
              runtime.setTeleSalesViewScope(scope);
              if (scope === "mine") {
                runtime.setTeleSalesFilters({
                  ...runtime.teleSalesFilters,
                  originalOwnerUserIds: [],
                });
              }
              runtime.setTeleSalesPage(1);
            }}
            onFiltersChange={(filters) => {
              if (filters.originalOwnerUserIds.length > 0) {
                runtime.setTeleSalesViewScope("all");
              }
              runtime.setTeleSalesFilters(filters);
              runtime.setTeleSalesPage(1);
            }}
            page={runtime.teleSalesPage}
            pageSize={runtime.teleSalesPageSize}
            total={runtime.teleSalesTotal}
            paginationLabel="电销任务池分页"
            onPageChange={runtime.setTeleSalesPage}
            onPageSizeChange={runtime.setTeleSalesPageSize}
            claimingItemId={runtime.claimingItemId}
            onClaim={(itemId) => void runtime.handleClaimItem(itemId)}
            batchClaiming={runtime.batchClaiming}
            onBatchClaim={(itemIds) => void runtime.handleClaimItems(itemIds)}
            publisherAction="claim"
            publisherActionLoading={runtime.ownerBatchClaiming}
            onBatchPublisherAction={(ownerUserId) =>
              void runtime.handleClaimItemsByOwner("tele_sales_pool", ownerUserId)
            }
          />
        </Tabs.TabPane>
        <Tabs.TabPane key="strong_follow" title="强跟任务池">
          <ItemTable
            canManage={canManage}
            items={runtime.strongFollowItems}
            publisherOptions={runtime.strongFollowPublisherOptions}
            originalOwnerOptions={runtime.strongFollowOriginalOwnerOptions}
            filters={runtime.strongFollowFilters}
            filterStorageKey="crm-collaboration-strong-follow-keyword"
            emptyText="当前暂无可见强跟任务"
            onFiltersChange={(filters) => {
              runtime.setStrongFollowFilters(filters);
              runtime.setStrongFollowPage(1);
            }}
            page={runtime.strongFollowPage}
            pageSize={runtime.strongFollowPageSize}
            total={runtime.strongFollowTotal}
            paginationLabel="强跟任务池分页"
            onPageChange={runtime.setStrongFollowPage}
            onPageSizeChange={runtime.setStrongFollowPageSize}
            claimingItemId={runtime.claimingItemId}
            onClaim={(itemId) => void runtime.handleClaimItem(itemId)}
            batchClaiming={runtime.batchClaiming}
            onBatchClaim={(itemIds) => void runtime.handleClaimItems(itemIds)}
            publisherAction="claim"
            publisherActionLoading={runtime.ownerBatchClaiming}
            onBatchPublisherAction={(ownerUserId) =>
              void runtime.handleClaimItemsByOwner("strong_follow_pool", ownerUserId)
            }
          />
        </Tabs.TabPane>
        <Tabs.TabPane key="my_items" title="我的协作任务">
          {canAccessContacts ? (
            <MyCollaborationContactTable
              items={runtime.myContacts}
              total={runtime.myItemsTotal}
              allTotal={runtime.myContactsTotal}
              teleSalesTotal={runtime.myContactsTeleSalesTotal}
              strongFollowTotal={runtime.myContactsStrongFollowTotal}
              page={runtime.myItemsPage}
              pageSize={runtime.myItemsPageSize}
              currentUserId={runtime.currentUserId}
              loading={runtime.loading}
              canManage={canManage}
              publisherOptions={runtime.myItemPublisherOptions}
              publisherActionLoading={runtime.ownerBatchReleasing}
              publisherReturnLoading={runtime.ownerBatchReturning}
              returningItemId={runtime.returningItemId}
              releaseMutating={runtime.releaseMutating}
              returnBatching={runtime.returnBatching}
              onPageChange={runtime.setMyItemsPage}
              onPageSizeChange={runtime.setMyItemsPageSize}
              taskType={myContactsTaskType}
              onTaskTypeChange={(taskType) => {
                setMyContactsTaskType(taskType);
                runtime.setMyItemsPage(1);
              }}
              onlyMine={onlyMyContacts}
              onOnlyMineChange={(nextOnlyMine) => {
                setOnlyMyContacts(nextOnlyMine);
                runtime.setMyItemsPage(1);
              }}
              onRefresh={() => void runtime.refreshMyContacts()}
              onOpenRecords={runtime.handleOpenContactRecords}
              onReturnToPool={(itemId) => void runtime.handleReturnItemToPool(itemId)}
              onRelease={(taskId, itemId) => void runtime.handleReleaseItem(taskId, itemId)}
              onBatchRelease={(items) => void runtime.handleReleaseItems(items)}
              onBatchPublisherAction={(ownerUserIds) =>
                void runtime.handleReleaseItemsByOwner(ownerUserIds)
              }
              onBatchPublisherReturn={(ownerUserIds) =>
                void runtime.handleReturnItemsToPoolByOwner(ownerUserIds)
              }
              onBatchReturn={(itemIds) => void runtime.handleReturnItemsToPool(itemIds)}
            />
          ) : (
            <section role="alert" className="crm-collaboration-task-error">
              当前账号缺少联系人访问权限，无法处理协作联系人。
            </section>
          )}
        </Tabs.TabPane>
      </Tabs>

      <Drawer
        className="crm-contact-workbench-detail-drawer"
        title={selectedContact ? `${selectedContact.contactName} - 联系动态` : "联系动态"}
        visible={Boolean(runtime.selectedContactId)}
        width={1420}
        footer={null}
        onCancel={() => {
          runtime.handleCloseContactRecords();
          setRecordDraft(createDefaultContactRecordDraft());
          setContactDraft(null);
          setDeleteConfirmRecord(null);
        }}
      >
        {runtime.contactRecordLoading ? (
          <div className="crm-contact-workbench-detail-loading">
            <Spin />
          </div>
        ) : (
          <div className="crm-contact-workbench-detail-grid">
            <ContactEditor
              draft={
                contactDraft ??
                (runtime.selectedContactDetail
                  ? createContactDraft(runtime.selectedContactDetail)
                  : null)
              }
              detail={runtime.selectedContactDetail}
              contactStarOptions={runtime.contactMetadata?.contactStarOptions ?? []}
              mutating={runtime.contactMutating}
              canEdit={canEditSelectedContact}
              onSearchOrganizations={runtime.searchOrganizations}
              onSearchOrganizationUnits={runtime.searchOrganizationUnits}
              onDraftChange={setContactDraft}
              onSave={() => void handleSaveContact()}
            />
            <ContactRecordList
              records={runtime.selectedContactRecords}
              mutating={runtime.contactRecordMutating}
              canDeleteRecord={canDeleteSelectedContactRecord}
              onDelete={setDeleteConfirmRecord}
            />
            <ContactRecordCreator
              key={`${runtime.selectedContactId}:${recordDraft.recordTypeCode}`}
              draft={recordDraft}
              recordTypeOptions={runtime.contactMetadata?.recordTypeOptions ?? []}
              recordLabelGroups={runtime.contactMetadata?.recordLabelGroups ?? []}
              mutating={runtime.contactRecordMutating}
              canRecord={canRecordSelectedContact}
              onDraftChange={setRecordDraft}
              onSubmit={() => void handleCreateRecord()}
            />
          </div>
        )}
      </Drawer>

      <Modal
        title="删除动态"
        visible={Boolean(deleteConfirmRecord)}
        confirmLoading={runtime.contactRecordMutating}
        onOk={() => void handleDeleteRecord()}
        onCancel={() => setDeleteConfirmRecord(null)}
      >
        确认删除这条动态吗？
      </Modal>

    </main>
  );
}

/** 以联系人为中心展示当前用户需要处理的协作任务。 */
function MyCollaborationContactTable({
  items,
  total,
  allTotal,
  teleSalesTotal,
  strongFollowTotal,
  page,
  pageSize,
  currentUserId,
  loading,
  canManage,
  publisherOptions,
  publisherActionLoading,
  publisherReturnLoading,
  returningItemId,
  releaseMutating,
  returnBatching,
  onPageChange,
  onPageSizeChange,
  taskType,
  onTaskTypeChange,
  onlyMine,
  onOnlyMineChange,
  onRefresh,
  onOpenRecords,
  onReturnToPool,
  onRelease,
  onBatchRelease,
  onBatchPublisherAction,
  onBatchPublisherReturn,
  onBatchReturn,
}: {
  items: ContactWorkbenchItem[];
  total: number;
  allTotal: number;
  teleSalesTotal: number;
  strongFollowTotal: number;
  page: number;
  pageSize: number;
  currentUserId: number;
  loading: boolean;
  canManage: boolean;
  publisherOptions: CrmCollaborationTaskPublisherOption[];
  publisherActionLoading: boolean;
  publisherReturnLoading: boolean;
  returningItemId?: number;
  releaseMutating: boolean;
  returnBatching: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  taskType: MyContactsTaskType;
  onTaskTypeChange: (taskType: MyContactsTaskType) => void;
  onlyMine: boolean;
  onOnlyMineChange: (onlyMine: boolean) => void;
  onRefresh: () => void;
  onOpenRecords: (contactId: number) => void;
  onReturnToPool: (itemId: number) => void;
  onRelease: (taskId: number, itemId: number) => void;
  onBatchRelease: (items: Array<{ taskId: number; itemId: number }>) => void;
  onBatchPublisherAction: (ownerUserIds: number[]) => void;
  onBatchPublisherReturn: (ownerUserIds: number[]) => void;
  onBatchReturn: (itemIds: number[]) => void;
}) {
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectedOwnerUserIds, setSelectedOwnerUserIds] = useState<number[]>([]);
  const [publisherDropdownVisible, setPublisherDropdownVisible] = useState(false);
  const selectedReleaseItems = items
    .filter((item) => selectedContactIds.includes(item.id))
    .flatMap((item) =>
      getMyTaskAssignments(item, currentUserId)
        .filter((assignment) => assignment.accessStatus === "claimed")
        .map((assignment) => ({ taskId: assignment.taskId, itemId: assignment.itemId })),
    );
  const selectedReturnItemIds = items
    .filter((item) => selectedContactIds.includes(item.id))
    .flatMap((item) =>
      getMyTaskAssignments(item, currentUserId)
        .filter(
          (assignment) =>
            assignment.accessStatus === "claimed" && assignment.assignmentMode === "pool",
        )
        .map((assignment) => assignment.itemId),
    );

  useEffect(() => {
    setSelectedContactIds((current) =>
      current.filter((contactId) => items.some((item) => item.id === contactId)),
    );
    setSelectedOwnerUserIds((current) =>
      current.filter((ownerUserId) =>
        publisherOptions.some((option) => option.ownerUserId === ownerUserId),
      ),
    );
  }, [items, publisherOptions]);

  const columns: TableColumnProps<ContactWorkbenchItem>[] = [
    {
      title: "联系人 / 职务",
      dataIndex: "contactName",
      width: 150,
      render: (_, item) => (
        <div className="crm-collaboration-contact-main">
          <strong>{item.contactName}</strong>
          <span>{item.positionName?.trim() || "职务待填"}</span>
        </div>
      ),
    },
    {
      title: "学校 / 院系",
      width: 260,
      render: (_, item) => (
        <div className="crm-collaboration-contact-main">
          <strong>{item.orgName || "-"}</strong>
          <span>
            {item.mainOrgUnitPathName?.trim() || item.mainOrgUnitName?.trim() || "院系待补"}
          </span>
        </div>
      ),
    },
    {
      title: "联系方式",
      width: 180,
      render: (_, item) => (
        <div className="crm-collaboration-contact-main">
          <span>{item.mobile || "-"}</span>
          <span>微信：{item.wechat || "-"}</span>
        </div>
      ),
    },
    {
      title: "任务信息",
      width: 260,
      render: (_, item) => {
        const assignments = getMyTaskAssignments(item, currentUserId);
        return assignments.length ? (
          <div className="crm-collaboration-contact-task-list">
            {assignments.map((assignment) => (
              <div key={assignment.itemId}>
                <strong>{assignment.taskName}</strong>
                <Space size={6}>
                  <Tag color="arcoblue">{formatTaskType(assignment.taskType)}</Tag>
                  <Typography.Text type="secondary">
                    {formatAccessStatus(assignment.accessStatus)}
                  </Typography.Text>
                </Space>
              </div>
            ))}
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      title: "最近动态",
      width: 260,
      render: (_, item) => {
        const summary = item.latestRecordContent || item.latestRecordMeetingName || "暂无动态，点击添加";
        return (
          <Popover
            position="left"
            trigger="hover"
            content={
              <div className="crm-collaboration-contact-record-popover">
                <strong>{item.latestRecordMeetingName || "最近动态"}</strong>
                <span>类型：{item.latestRecordTypeLabel || "-"}</span>
                <span>时间：{item.latestRecordAt || "-"}</span>
                <span>内容：{item.latestRecordContent || item.latestRecordMeetingName || "暂无动态内容"}</span>
              </div>
            }
          >
            <Button
              className="crm-collaboration-contact-record-link"
              type="text"
              size="small"
              onClick={() => onOpenRecords(item.id)}
            >
              <span>{summary}</span>
              {item.latestRecordAt ? <small>{item.latestRecordAt}</small> : null}
            </Button>
          </Popover>
        );
      },
    },
    {
      title: "操作",
      width: 190,
      fixed: "right",
      render: (_, item) => {
        const claimedAssignments = getMyTaskAssignments(item, currentUserId).filter(
          (assignment) => assignment.accessStatus === "claimed",
        );
        return claimedAssignments.length ? (
          <div className="crm-collaboration-contact-actions">
            {claimedAssignments.map((assignment) => (
              <div className="crm-collaboration-contact-action-group" key={assignment.itemId}>
                {assignment.assignmentMode === "pool" ? (
                  <Button
                    size="mini"
                    status="warning"
                    disabled={
                      !canManage ||
                      releaseMutating ||
                      returnBatching ||
                      publisherReturnLoading ||
                      publisherActionLoading
                    }
                    loading={returningItemId === assignment.itemId}
                    onClick={() => onReturnToPool(assignment.itemId)}
                  >
                    退回任务池
                  </Button>
                ) : null}
                <Button
                  size="mini"
                  disabled={
                    !canManage ||
                    releaseMutating ||
                    returnBatching ||
                    publisherReturnLoading ||
                    publisherActionLoading ||
                    returningItemId !== undefined
                  }
                  onClick={() => onRelease(assignment.taskId, assignment.itemId)}
                >
                  完成并释放权限
                </Button>
              </div>
            ))}
          </div>
        ) : (
          "-"
        );
      },
    },
  ];

  return (
    <section className="crm-list-panel crm-collaboration-task-card">
      <div className="crm-collaboration-contact-toolbar">
        <Space size={8} wrap>
          <Button
            size="small"
            icon={<ListFilter size={14} />}
            type={onlyMine ? "primary" : "secondary"}
            aria-pressed={onlyMine}
            onClick={() => onOnlyMineChange(!onlyMine)}
          >
            仅看我处理的协作联系人
          </Button>
          <Button
            size="small"
            type={taskType === "all" ? "primary" : "secondary"}
            aria-pressed={taskType === "all"}
            onClick={() => onTaskTypeChange("all")}
          >
            全部 {allTotal}
          </Button>
          <Button
            size="small"
            type={taskType === "tele_sales" ? "primary" : "secondary"}
            aria-pressed={taskType === "tele_sales"}
            onClick={() => onTaskTypeChange("tele_sales")}
          >
            电销 {teleSalesTotal}
          </Button>
          <Button
            size="small"
            type={taskType === "strong_follow" ? "primary" : "secondary"}
            aria-pressed={taskType === "strong_follow"}
            onClick={() => onTaskTypeChange("strong_follow")}
          >
            强跟 {strongFollowTotal}
          </Button>
        </Space>
        <Button
          size="small"
          icon={<RefreshCw size={14} />}
          loading={loading}
          onClick={onRefresh}
        >
          刷新
        </Button>
      </div>
      <div className="crm-list-section-header">
        <div className="crm-collaboration-contact-header-main">
          <h2>待处理联系人</h2>
          <p>联系人本身就是工作对象；最近认领的排在后面</p>
        </div>
        <Space>
          <span className="crm-collaboration-contact-total">共 {total} 位</span>
          <Dropdown
            trigger="click"
            position="bl"
            popupVisible={publisherDropdownVisible}
            onVisibleChange={setPublisherDropdownVisible}
            triggerProps={{ escToClose: true }}
            droplist={(
              <div
                className="crm-collaboration-task-publisher-dropdown"
                role="group"
                aria-label="已发布任务的人"
              >
                <div className="crm-collaboration-task-publisher-dropdown-title">
                  选择发布人（可多选）
                </div>
                {publisherOptions.length === 0 ? <div>暂无已认领任务的发布人</div> : null}
                {publisherOptions.map((option) => (
                  <Checkbox
                    key={option.ownerUserId}
                    aria-label={`选择发布人 ${option.ownerUserName}`}
                    checked={selectedOwnerUserIds.includes(option.ownerUserId)}
                    disabled={
                      !canManage ||
                      releaseMutating ||
                      publisherActionLoading ||
                      publisherReturnLoading
                    }
                    onChange={(checked) =>
                      setSelectedOwnerUserIds((current) =>
                        checked
                          ? [...new Set([...current, option.ownerUserId])]
                          : current.filter((id) => id !== option.ownerUserId),
                      )
                    }
                  >
                    {option.ownerUserName}（已认领 {option.itemCount} 条）
                  </Checkbox>
                ))}
                <Button
                  type="primary"
                  size="small"
                  loading={publisherActionLoading}
                  disabled={
                    !canManage ||
                    selectedOwnerUserIds.length === 0 ||
                    releaseMutating ||
                    publisherReturnLoading
                  }
                  onClick={() => {
                    onBatchPublisherAction(selectedOwnerUserIds);
                    setPublisherDropdownVisible(false);
                  }}
                >
                  批量完成并释放权限
                </Button>
                <Button
                  size="small"
                  loading={publisherReturnLoading}
                  disabled={
                    !canManage ||
                    selectedOwnerUserIds.length === 0 ||
                    releaseMutating ||
                    publisherActionLoading
                  }
                  onClick={() => {
                    onBatchPublisherReturn(selectedOwnerUserIds);
                    setPublisherDropdownVisible(false);
                  }}
                >
                  批量退回任务池
                </Button>
              </div>
            )}
          >
            <Button size="small">按发布人完成</Button>
          </Dropdown>
          {selectedReturnItemIds.length > 0 ? (
            <Button
              size="small"
              loading={returnBatching}
              disabled={
                !canManage ||
                returningItemId !== undefined ||
                releaseMutating ||
                publisherReturnLoading ||
                publisherActionLoading
              }
              onClick={() => onBatchReturn(selectedReturnItemIds)}
            >
              批量退回任务池
            </Button>
          ) : null}
          {selectedReleaseItems.length > 0 ? (
            <Button
              type="primary"
              size="small"
              loading={releaseMutating}
              disabled={
                !canManage ||
                returningItemId !== undefined ||
                returnBatching ||
                publisherReturnLoading ||
                publisherActionLoading
              }
              onClick={() => onBatchRelease(selectedReleaseItems)}
            >
              批量完成并释放权限
            </Button>
          ) : null}
        </Space>
      </div>
      <div className="crm-list-panel-body">
        <Table
          rowKey="id"
          columns={columns}
          data={items}
          loading={loading}
          pagination={false}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedContactIds,
            onChange: (keys) => setSelectedContactIds(keys.map(Number)),
            checkboxProps: (item) => ({
              disabled:
                !canManage ||
                releaseMutating ||
                returnBatching ||
                publisherReturnLoading ||
                publisherActionLoading ||
                !getMyTaskAssignments(item, currentUserId).some(
                  (assignment) => assignment.accessStatus === "claimed",
                ),
              "aria-label": `选择完成任务联系人 ${item.contactName}`,
            }),
          }}
          noDataElement="当前暂无已认领或指定给我的联系人任务"
          scroll={{ x: 1450 }}
        />
        <Pagination
          className="crm-collaboration-contact-pagination"
          current={page}
          pageSize={pageSize}
          total={total}
          sizeCanChange
          sizeOptions={[...CRM_COLLABORATION_PAGE_SIZE_OPTIONS]}
          showTotal
          showJumper
          onChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
    </section>
  );
}

/** 只保留当前用户仍需处理的活动任务关系。 */
function getMyTaskAssignments(
  item: ContactWorkbenchItem,
  currentUserId: number,
): ContactWorkbenchTemporaryTaskAssignment[] {
  return item.temporaryTaskAssignments.filter(
    (assignment) =>
      assignment.handlerUserId === currentUserId &&
      (assignment.accessStatus === "assigned" || assignment.accessStatus === "claimed"),
  );
}

/** 员工选择器，避免协作任务继续手填用户 ID。 */
function StaffSelect({
  testId,
  placeholder,
  value,
  options,
  loading,
  allowClear = false,
  onChange,
}: {
  testId: string;
  placeholder: string;
  value?: number;
  options: CrmCollaborationStaffOption[];
  loading: boolean;
  allowClear?: boolean;
  onChange: (value: number | undefined) => void;
}) {
  return (
    <Select
      data-testid={testId}
      allowClear={allowClear}
      showSearch
      loading={loading}
      placeholder={placeholder}
      value={value}
      filterOption={(inputValue, option) => {
        const searchText = String(option.props.searchText ?? "");
        return searchText.toLowerCase().includes(inputValue.toLowerCase());
      }}
      onChange={(nextValue) => onChange(nextValue ? Number(nextValue) : undefined)}
    >
      {options.map((option) => (
        <Select.Option
          key={option.id}
          value={option.id}
          disabled={option.disabled}
          searchText={`${option.displayName} ${option.username} ${option.mainDeptName} ${option.id}`}
        >
          {formatStaffOptionLabel(option)}
        </Select.Option>
      ))}
    </Select>
  );
}

/** 格式化员工候选，便于业务人员按姓名、账号和部门识别。 */
function formatStaffOptionLabel(option: CrmCollaborationStaffOption) {
  const dept = option.mainDeptName ? ` - ${option.mainDeptName}` : "";
  return `${option.displayName} ${option.username}${dept}`;
}

/** 读取本地文本/CSV 文件，后续仍交给导入校验接口解析 ID。 */
async function readImportTextFile(file: File) {
  return file.text();
}

/** 展示协作任务批次及发布、取消操作。 */
function TaskTable({
  items,
  loading,
  canManage,
  onPublish,
  onCancel,
}: {
  items: CrmCollaborationTask[];
  loading: boolean;
  canManage: boolean;
  onPublish: (taskId: number) => void;
  onCancel: (taskId: number) => void;
}) {
  return (
    <section className="crm-list-panel crm-collaboration-task-card">
      <div className="crm-list-section-header">
        <h2>任务批次</h2>
      </div>
      <div className="crm-list-panel-body">
      <div className="crm-collaboration-task-table-wrap">
        <table className="crm-collaboration-task-table">
          <thead>
            <tr>
              <th>任务名称</th>
              <th>类型</th>
              <th>对象</th>
              <th>分配</th>
              <th>状态</th>
              <th>对象数</th>
              <th>状态数量</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>正在加载协作任务...</td>
              </tr>
            ) : null}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={8}>暂无协作任务批次。</td>
              </tr>
            ) : null}
            {items.map((task) => (
              <tr key={task.id}>
                <td>{task.taskName}</td>
                <td>{formatTaskType(task.taskType)}</td>
                <td>{formatTargetType(task.targetType)}</td>
                <td>{formatAssignmentMode(task.assignmentMode)}</td>
                <td>
                  <Tag color={task.taskStatus === "active" ? "green" : "arcoblue"}>{formatTaskStatus(task.taskStatus)}</Tag>
                </td>
                <td>{task.totalItemCount}</td>
                <td>
                  可认领 {task.statusCounts.available} / 已指定 {task.statusCounts.assigned} / 已认领{" "}
                  {task.statusCounts.claimed} / 已释放 {task.statusCounts.released}
                </td>
                <td>
                  <Space>
                    <Button size="mini" disabled={!canManage || task.totalItemCount <= 0 || task.taskStatus === "active"} onClick={() => onPublish(task.id)}>
                      发布
                    </Button>
                    <Button size="mini" status="danger" disabled={!canManage || task.taskStatus === "cancelled"} onClick={() => onCancel(task.id)}>
                      取消
                    </Button>
                  </Space>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </section>
  );
}

/** 展示任务池对象及认领、释放操作。 */
function ItemTable({
  items,
  publisherOptions = EMPTY_PUBLISHER_OPTIONS,
  originalOwnerOptions = EMPTY_ORIGINAL_OWNER_OPTIONS,
  filters = EMPTY_CRM_COLLABORATION_POOL_FILTERS,
  filterStorageKey = "crm-collaboration-pool-keyword",
  emptyText,
  canManage,
  viewScope,
  onViewScopeChange,
  onFiltersChange,
  page,
  pageSize,
  total,
  paginationLabel,
  onPageChange,
  onPageSizeChange,
  claimingItemId,
  batchClaiming,
  releaseBatching,
  publisherAction,
  publisherActionLoading,
  onClaim,
  onBatchClaim,
  onBatchPublisherAction,
  onBatchRelease,
  onRelease,
}: {
  items: CrmCollaborationTaskItem[];
  publisherOptions?: CrmCollaborationTaskPublisherOption[];
  originalOwnerOptions?: CrmCollaborationTaskOriginalOwnerOption[];
  filters?: CrmCollaborationPoolListFilters;
  filterStorageKey?: string;
  emptyText: string;
  canManage: boolean;
  viewScope?: "all" | "mine";
  onViewScopeChange?: (scope: "all" | "mine") => void;
  onFiltersChange?: (filters: CrmCollaborationPoolListFilters) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  paginationLabel?: string;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  claimingItemId?: number;
  batchClaiming?: boolean;
  releaseBatching?: boolean;
  publisherAction?: "claim" | "release";
  publisherActionLoading?: boolean;
  onClaim?: (itemId: number) => void;
  onBatchClaim?: (itemIds: number[]) => void;
  onBatchPublisherAction?: (ownerUserIds: number[]) => void;
  onBatchRelease?: (items: Array<{ taskId: number; itemId: number }>) => void;
  onRelease?: (taskId: number, itemId: number) => void;
}) {
  const claimableItems = items.filter((item) => item.accessStatus === "available");
  const releasableItems = items.filter((item) => item.accessStatus === "claimed");
  const selectableItems = onBatchRelease ? releasableItems : claimableItems;
  const provinceOptions = useMemo(() => getProvinceOptions(), []);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [selectedOwnerUserIds, setSelectedOwnerUserIds] = useState<number[]>([]);
  const [selectedOriginalOwnerUserIds, setSelectedOriginalOwnerUserIds] = useState<number[]>(
    filters.originalOwnerUserIds,
  );
  const [filterDraft, setFilterDraft] = useState<CrmCollaborationPoolListFilters>(filters);
  const [publisherDropdownVisible, setPublisherDropdownVisible] = useState(false);
  const [originalOwnerDropdownVisible, setOriginalOwnerDropdownVisible] = useState(false);
  const [originalOwnerSearchKeyword, setOriginalOwnerSearchKeyword] = useState("");
  const selectedEligibleIds = selectedItemIds.filter((itemId) => selectableItems.some((item) => item.id === itemId));
  const allEligibleSelected = selectableItems.length > 0 && selectedEligibleIds.length === selectableItems.length;
  const normalizedOriginalOwnerKeyword = originalOwnerSearchKeyword.trim().toLowerCase();
  const filteredOriginalOwnerOptions = normalizedOriginalOwnerKeyword
    ? originalOwnerOptions.filter((option) =>
        option.originalOwnerUserName.toLowerCase().includes(normalizedOriginalOwnerKeyword),
      )
    : originalOwnerOptions;

  useEffect(() => {
    setSelectedItemIds((current) => current.filter((itemId) => items.some((item) => item.id === itemId)));
    setSelectedOwnerUserIds((current) =>
      current.filter((ownerUserId) =>
        publisherOptions.some((option) => option.ownerUserId === ownerUserId),
      ),
    );
  }, [items, publisherOptions]);

  useEffect(() => {
    setFilterDraft(filters);
    setSelectedOriginalOwnerUserIds(filters.originalOwnerUserIds);
  }, [filters]);

  /** 更新当前页任务对象的勾选状态。 */
  function toggleItem(itemId: number, checked: boolean) {
    setSelectedItemIds((current) => checked ? [...new Set([...current, itemId])] : current.filter((id) => id !== itemId));
  }

  /** 更新下拉框中发布人的多选状态。 */
  function toggleOwner(ownerUserId: number, checked: boolean) {
    setSelectedOwnerUserIds((current) =>
      checked
        ? [...new Set([...current, ownerUserId])]
        : current.filter((id) => id !== ownerUserId),
    );
  }

  /** 更新原负责人筛选多选状态。 */
  function toggleOriginalOwner(ownerUserId: number, checked: boolean) {
    setSelectedOriginalOwnerUserIds((current) =>
      checked
        ? [...new Set([...current, ownerUserId])]
        : current.filter((id) => id !== ownerUserId),
    );
  }

  /** 应用关键词、省份与学校筛选。 */
  function applyTextFilters() {
    onFiltersChange?.({
      ...filters,
      keyword: filterDraft.keyword.trim(),
      province: filterDraft.province.trim(),
      orgName: filterDraft.orgName.trim(),
    });
  }

  /** 应用原负责人筛选。 */
  function applyOriginalOwnerFilters() {
    onFiltersChange?.({
      ...filters,
      originalOwnerUserIds: selectedOriginalOwnerUserIds,
    });
    setOriginalOwnerDropdownVisible(false);
  }

  /** 清空全部任务池筛选。 */
  function resetFilters() {
    setFilterDraft(EMPTY_CRM_COLLABORATION_POOL_FILTERS);
    setSelectedOriginalOwnerUserIds([]);
    onFiltersChange?.(EMPTY_CRM_COLLABORATION_POOL_FILTERS);
  }

  return (
    <section className="crm-list-panel crm-collaboration-task-card">
      {onFiltersChange ? (
        <div className="crm-list-filter-panel crm-collaboration-pool-filter-panel">
          <div className="crm-list-filter-fields">
            <div className="crm-list-filter-primary">
              <label className="crm-list-field crm-list-field--lg">
                <SearchHistoryInput
                  storageKey={filterStorageKey}
                  value={filterDraft.keyword}
                  placeholder="任务名称 / 联系人"
                  allowClear
                  onChange={(keyword) => setFilterDraft((current) => ({ ...current, keyword }))}
                  onPressEnter={applyTextFilters}
                />
              </label>
              <label className="crm-list-field crm-list-field--md">
                <Select
                  allowClear
                  showSearch
                  placeholder="省份"
                  data-testid="collaboration-pool-province-filter"
                  value={filterDraft.province || undefined}
                  filterOption={(inputValue, option) =>
                    String(option.props.value ?? "")
                      .toLowerCase()
                      .includes(inputValue.trim().toLowerCase())
                  }
                  onChange={(province) =>
                    setFilterDraft((current) => ({
                      ...current,
                      province: typeof province === "string" ? province : "",
                    }))
                  }
                >
                  {provinceOptions.map((province) => (
                    <Select.Option key={province} value={province}>
                      {province}
                    </Select.Option>
                  ))}
                </Select>
              </label>
              <label className="crm-list-field crm-list-field--md">
                <Input
                  allowClear
                  value={filterDraft.orgName}
                  placeholder="学校"
                  onChange={(orgName) => setFilterDraft((current) => ({ ...current, orgName }))}
                  onPressEnter={applyTextFilters}
                />
              </label>
            </div>
          </div>
          <div className="crm-list-filter-actions">
            <Button type="primary" onClick={applyTextFilters}>
              查询
            </Button>
            <Button onClick={resetFilters}>重置</Button>
          </div>
        </div>
      ) : null}
      <div className="crm-list-section-header">
        <div className="crm-collaboration-task-header-main">
          <h2>任务对象</h2>
          {viewScope && onViewScopeChange ? (
            <Radio.Group
              type="button"
              size="small"
              value={viewScope}
              onChange={(value) => onViewScopeChange(value as "all" | "mine")}
              aria-label="电销任务池范围"
            >
              <Radio value="all">全部任务</Radio>
              <Radio value="mine">我发的任务</Radio>
            </Radio.Group>
          ) : null}
        </div>
        {onBatchClaim && selectedEligibleIds.length > 0 ? (
          <Button
            type="primary"
            size="small"
            loading={batchClaiming}
            disabled={!canManage || batchClaiming}
            onClick={() => onBatchClaim(selectedEligibleIds)}
          >
            批量认领
          </Button>
        ) : null}
        {onBatchRelease && selectedEligibleIds.length > 0 ? (
          <Button
            type="primary"
            size="small"
            loading={releaseBatching}
            disabled={!canManage || releaseBatching}
            onClick={() =>
              onBatchRelease(
                selectedEligibleIds.map((itemId) => {
                  const item = items.find((candidate) => candidate.id === itemId);
                  return { taskId: item!.taskId, itemId };
                }),
              )
            }
          >
            批量释放
          </Button>
        ) : null}
      </div>
      <div className="crm-list-panel-body">
      <div className="crm-collaboration-task-table-wrap">
        <table className="crm-collaboration-task-table">
          <thead>
            <tr>
              {onBatchClaim || onBatchRelease ? (
                <th className="crm-collaboration-task-selection-column">
                  <Checkbox
                    aria-label={onBatchRelease ? "全选可释放任务" : "全选可认领任务"}
                    checked={allEligibleSelected}
                    indeterminate={selectedEligibleIds.length > 0 && !allEligibleSelected}
                    disabled={!canManage || selectableItems.length === 0 || batchClaiming || releaseBatching}
                    onChange={(checked) => setSelectedItemIds(checked ? selectableItems.map((item) => item.id) : [])}
                  />
                </th>
              ) : null}
              <th>
                {publisherAction && onBatchPublisherAction ? (
                  <Dropdown
                    trigger="click"
                    position="bl"
                    popupVisible={publisherDropdownVisible}
                    onVisibleChange={setPublisherDropdownVisible}
                    triggerProps={{ escToClose: true }}
                    droplist={(
                      <div
                        className="crm-collaboration-task-publisher-dropdown"
                        role="group"
                        aria-label="已发布任务的人"
                      >
                        <div className="crm-collaboration-task-publisher-dropdown-title">
                          选择发布人（可多选）
                        </div>
                        {publisherOptions.length === 0 ? (
                          <div>暂无可{publisherAction === "claim" ? "认领" : "释放"}任务的发布人</div>
                        ) : null}
                        {publisherOptions.map((option) => (
                          <Checkbox
                            key={option.ownerUserId}
                            aria-label={`选择发布人 ${option.ownerUserName}`}
                            checked={selectedOwnerUserIds.includes(option.ownerUserId)}
                            disabled={
                              !canManage ||
                              batchClaiming ||
                              releaseBatching ||
                              publisherActionLoading
                            }
                            onChange={(checked) => toggleOwner(option.ownerUserId, checked)}
                          >
                            {option.ownerUserName}（可{publisherAction === "claim" ? "认领" : "释放"} {option.itemCount} 条）
                          </Checkbox>
                        ))}
                        <Button
                          type="primary"
                          size="small"
                          loading={publisherActionLoading}
                          disabled={
                            !canManage ||
                            selectedOwnerUserIds.length === 0 ||
                            batchClaiming ||
                            releaseBatching ||
                            publisherActionLoading
                          }
                          onClick={() => {
                            onBatchPublisherAction(selectedOwnerUserIds);
                            setPublisherDropdownVisible(false);
                          }}
                        >
                          {publisherAction === "claim" ? "认领" : "释放"}所选发布人的全部任务
                        </Button>
                      </div>
                    )}
                  >
                    <Button
                      type="text"
                      size="small"
                      aria-label="任务"
                      aria-haspopup="dialog"
                      aria-expanded={publisherDropdownVisible}
                    >
                      任务
                    </Button>
                  </Dropdown>
                ) : (
                  "任务"
                )}
              </th>
              <th>任务对象</th>
              <th>学校</th>
              <th>组织单元</th>
              <th>
                {onFiltersChange ? (
                  <Dropdown
                    trigger="click"
                    position="bl"
                    popupVisible={originalOwnerDropdownVisible}
                    onVisibleChange={(visible) => {
                      setOriginalOwnerDropdownVisible(visible);
                      if (!visible) {
                        setOriginalOwnerSearchKeyword("");
                      }
                    }}
                    triggerProps={{ escToClose: true }}
                    droplist={(
                      <div
                        className="crm-collaboration-task-publisher-dropdown"
                        role="group"
                        aria-label="按原负责人筛选"
                      >
                        <div className="crm-collaboration-task-publisher-dropdown-title">
                          选择原负责人（可多选）
                        </div>
                        <Input
                          allowClear
                          size="small"
                          placeholder="模糊搜索原负责人"
                          value={originalOwnerSearchKeyword}
                          aria-label="模糊搜索原负责人"
                          onChange={setOriginalOwnerSearchKeyword}
                        />
                        {originalOwnerOptions.length === 0 ? (
                          <div>暂无可筛选的原负责人</div>
                        ) : filteredOriginalOwnerOptions.length === 0 ? (
                          <div>未找到匹配的原负责人</div>
                        ) : null}
                        <div className="crm-collaboration-task-owner-filter-list">
                          {filteredOriginalOwnerOptions.map((option) => (
                            <Checkbox
                              key={option.originalOwnerUserId}
                              aria-label={`选择原负责人 ${option.originalOwnerUserName}`}
                              checked={selectedOriginalOwnerUserIds.includes(option.originalOwnerUserId)}
                              onChange={(checked) =>
                                toggleOriginalOwner(option.originalOwnerUserId, checked)
                              }
                            >
                              {option.originalOwnerUserName}（共 {option.itemCount} 条）
                            </Checkbox>
                          ))}
                        </div>
                        <Space>
                          <Button
                            type="primary"
                            size="small"
                            disabled={selectedOriginalOwnerUserIds.length === 0}
                            onClick={applyOriginalOwnerFilters}
                          >
                            按所选原负责人筛选
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setSelectedOriginalOwnerUserIds([]);
                              setOriginalOwnerSearchKeyword("");
                              onFiltersChange({
                                ...filters,
                                originalOwnerUserIds: [],
                              });
                              setOriginalOwnerDropdownVisible(false);
                            }}
                          >
                            清空
                          </Button>
                        </Space>
                      </div>
                    )}
                  >
                    <Button
                      type="text"
                      size="small"
                      aria-label="原负责人"
                      aria-haspopup="dialog"
                      aria-expanded={originalOwnerDropdownVisible}
                    >
                      原负责人
                      {filters.originalOwnerUserIds.length > 0
                        ? `（${filters.originalOwnerUserIds.length}）`
                        : ""}
                    </Button>
                  </Dropdown>
                ) : (
                  "原负责人"
                )}
              </th>
              <th>指定人</th>
              <th>认领人</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={onBatchClaim || onBatchRelease ? 10 : 9}>{emptyText}</td>
              </tr>
            ) : null}
            {items.map((item) => (
              <tr key={item.id}>
                {onBatchClaim || onBatchRelease ? (
                  <td className="crm-collaboration-task-selection-column">
                    <Checkbox
                      aria-label={`${onBatchRelease ? "选择释放任务对象" : "选择任务对象"} ${item.targetName ?? item.id}`}
                      checked={selectedEligibleIds.includes(item.id)}
                      disabled={!selectableItems.some((candidate) => candidate.id === item.id) || !canManage || batchClaiming || releaseBatching}
                      onChange={(checked) => toggleItem(item.id, checked)}
                    />
                  </td>
                ) : null}
                <td>{item.taskName ?? "-"}</td>
                <td>{item.targetName ?? "-"}</td>
                <td>{item.orgName ?? "-"}</td>
                <td>{item.orgUnitName ?? "-"}</td>
                <td>{item.originalOwnerUserName ?? "-"}</td>
                <td>{item.assignedUserName ?? "-"}</td>
                <td>{item.claimedByUserName ?? "-"}</td>
                <td>{formatAccessStatus(item.accessStatus)}</td>
                <td>
                  {onClaim && item.accessStatus === "available" ? (
                    <Button
                      size="mini"
                      type="primary"
                      loading={claimingItemId === item.id}
                      disabled={!canManage || claimingItemId !== undefined || batchClaiming}
                      onClick={() => onClaim(item.id)}
                    >
                      认领
                    </Button>
                  ) : null}
                  {onRelease && item.accessStatus === "claimed" ? (
                    <Button size="mini" disabled={!canManage || releaseBatching} onClick={() => onRelease(item.taskId, item.id)}>
                      释放
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {page !== undefined &&
      pageSize !== undefined &&
      total !== undefined &&
      onPageChange &&
      onPageSizeChange &&
      paginationLabel ? (
        <nav aria-label={paginationLabel}>
          <Pagination
            className="crm-collaboration-contact-pagination"
            current={page}
            pageSize={pageSize}
            total={total}
            sizeCanChange
            sizeOptions={[...CRM_COLLABORATION_PAGE_SIZE_OPTIONS]}
            showTotal
            showJumper
            onChange={(nextPage) => onPageChange(nextPage)}
            onPageSizeChange={onPageSizeChange}
          />
        </nav>
      ) : null}
      </div>
    </section>
  );
}
